<script setup lang="ts">
const localePath = useLocalePath();
const { t, tm, rt } = useI18n() as any;

const activeExample = ref(0);

const scrollToPricing = () => {
  const element = document.getElementById('pricing');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const apiExamples = ref([
  {
    title: t('viberOtp.api.basicRequest.title'),
    description: t('viberOtp.api.basicRequest.description'),
    language: 'JSON',
    code: `{
  "messages": [
    {
      "from": "ALPHANAME",
      "to": "380936670003",
      "type": "viber_otp",
      "text": "",
      "ttl": "90",
      "messageData": {
        "templateId": "6c929cef-29b4-4349-bc9d-2a07bdbb6e43",
        "templateLang": "uk",
        "templateParams": {
          "pin": 321,
          "business_platform_name": "SMSBAT",
          "code_validity_time": 7
        }
      }
    }
  ]
}`
  },
  {
    title: t('viberOtp.api.basicOtp.title'),
    description: t('viberOtp.api.basicOtp.description'),
    language: 'JSON',
    code: `{
  "messages": [
    {
      "from": "YOUR_BRAND",
      "to": "380936670003",
      "type": "viber_otp",
      "messageData": {
        "templateId": "0aac888f-2ee2-4112-9659-1755a951966a",
        "templateLang": "en",
        "templateParams": {
          "pin": "123456"
        }
      }
    }
  ]
}`
  },
  {
    title: t('viberOtp.api.otpWithValidity.title'),
    description: t('viberOtp.api.otpWithValidity.description'),
    language: 'JSON',
    code: `{
  "messages": [
    {
      "from": "YOUR_BRAND",
      "to": "380936670003",
      "type": "viber_otp",
      "messageData": {
        "templateId": "bed0942f-e07e-4879-834b-29cc4cf3ec35",
        "templateLang": "en",
        "templateParams": {
          "pin": "123456",
          "code_validity_time": 5
        }
      }
    }
  ]
}`
  }
]);

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(apiExamples.value[activeExample.value].code);
  } catch (error) {
    console.error('Failed to copy code:', error);
  }
};

// SEO
useHead({
  titleTemplate: () => t("seo.layouts./viber-otp.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-otp.description") },
    { name: "keywords", content: "Viber OTP, One-Time Password, Viber Business Messages, SMSBAT, verification, authentication, secure messaging" },
    { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" },
    { property: "og:title", content: () => t("seo.layouts./viber-otp.title") },
    { property: "og:description", content: () => t("seo.layouts./viber-otp.description") },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "SMSBAT" },
    { property: "og:image", content: "/images/viber-secureplus-messages.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Viber OTP Service",
        "description": t("seo.layouts./viber-otp.description"),
        "url": "https://smsbat.com/viber-otp",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0.0000",
          "priceCurrency": "EUR",
          "description": "Starting from free for some countries"
        },
        "provider": {
          "@type": "Organization",
          "name": "SMSBAT",
          "url": "https://smsbat.com"
        },
        "featureList": [
          "Secure OTP Delivery",
          "Multiple Templates",
          "Global Coverage",
          "Real-time Delivery",
          "API Integration",
          "Template Customization"
        ]
      })
    }
  ]
});

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})

const replacedBrackets = (text: string) => {
  return text.replace('⸨', '{{').replace('⸩', '}}')
}

</script>

<template>
  <div>
    <div data-v-96050afa="" class="page-section">
      <div data-v-96050afa="" class="container">
        <div data-v-dafc0bc8="" class="banner-image-container">
          <NuxtImg src="/images/viber-secureplus-messages.png" width="1200" height="800" alt="Viber SecurePlus Messages" format="webp" loading="lazy" class="w-full h-auto object-contain" />
        </div>
        <div data-v-96050afa="" class="section-head">
          <div data-v-96050afa="" class="section-head-title">{{ t('viberOtp.headSection.title') }}</div>
        </div>
        <div data-v-96050afa="" class="section-content mb-20">
          <div data-v-96050afa="" class="text-content">
            <div class="text-content">{{ t('viberOtp.headSection.subtitle') }}</div>
            <p data-v-96050afa="">
              {{ t('viberOtp.headSection.text1') }}<br />{{ t('viberOtp.headSection.text2') }}
            </p>
          </div>
          <div class="features-grid" data-v-96050afa="">
            <div data-v-96050afa="" class="feature-card">
              <div data-v-96050afa="" class="feature-icon"
                style="width:64px;height:64px;margin:0 auto 20px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg, rgba(123, 81, 157, 0.1) 0%, rgba(123, 81, 157, 0.05) 100%);border-radius:16px;transition:all 0.3s ease;">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" fill="#3070b6">
                  </path>
                  <path d="M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z" fill="white"></path>
                </svg>
              </div>
              <h3 data-v-96050afa="" class="feature-title">{{ t('viberOtp.headSection.blocks[0].title') }}</h3>
              <p data-v-96050afa="" class="feature-description">{{ t('viberOtp.headSection.blocks[0].subtitle') }}</p>
            </div>

            <div data-v-96050afa="" class="feature-card">
              <div data-v-96050afa="" class="feature-icon"
                style="width:64px;height:64px;margin:0 auto 20px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg, rgba(123, 81, 157, 0.1) 0%, rgba(123, 81, 157, 0.05) 100%);border-radius:16px;transition:all 0.3s ease;">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16 6L18.29 8.29L13.41 13.17L9.41 9.17L2 16.59L3.41 18L9.41 12L13.41 16L19.71 9.71L22 12V6H16Z"
                    fill="#3070b6"></path>
                </svg>
              </div>
              <h3 data-v-96050afa="" class="feature-title">{{ t('viberOtp.headSection.blocks[1].title') }}</h3>
              <p data-v-96050afa="" class="feature-description">{{ t('viberOtp.headSection.blocks[1].subtitle') }}</p>
            </div>

            <div data-v-96050afa="" class="feature-card">
              <div data-v-96050afa="" class="feature-icon"
                style="width:64px;height:64px;margin:0 auto 20px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg, rgba(123, 81, 157, 0.1) 0%, rgba(123, 81, 157, 0.05) 100%);border-radius:16px;transition:all 0.3s ease;">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 7C13.4 7 14.8 8.6 14.8 10.5C14.8 12.4 13.4 14 12 14C10.6 14 9.2 12.4 9.2 10.5C9.2 8.6 10.6 7 12 7ZM18 18C16.5 16 14 15 12 15C10 15 7.5 16 6 18V19H18V18Z"
                    fill="#3070b6"></path>
                </svg>
              </div>
              <h3 data-v-96050afa="" class="feature-title">{{ t('viberOtp.headSection.blocks[2].title') }}</h3>
              <p data-v-96050afa="" class="feature-description">{{ t('viberOtp.headSection.blocks[2].subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hero Section -->
    <div class="main-banner">
      <div class="main-banner-content">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('viberOtp.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('viberOtp.hero.subtitle') }}
          </div>
          <div class="banner-description mt-4">
            {{ $t('viberOtp.hero.description') }}
          </div>
          <div class="banner-action">
            <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
              <p>{{ $t('viberOtp.actions.getStarted') }}</p>
            </NuxtLink>
            <button @click="scrollToPricing" class="button outlined">
              <p>{{ $t('viberOtp.actions.viewPricing') }}</p>
            </button>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg src="/images/viber_otp.png" class="mt-20 w-full h-auto object-contain" alt="Viber OTP Messages" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" />
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberOtp.stats.title') }}</div>
          <div class="section-head-description">{{ $t('viberOtp.stats.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">63%</div>
              <div class="stat-label">{{ $t('viberOtp.stats.primeActivity') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">47%</div>
              <div class="stat-label">{{ $t('viberOtp.stats.women') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">53%</div>
              <div class="stat-label">{{ $t('viberOtp.stats.men') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">78%</div>
              <div class="stat-label">{{ $t('viberOtp.stats.mobile') }}</div>
            </div>
          </div>
          <div class="top-countries">
            <h3>{{ $t('viberOtp.stats.topCountries.title') }}</h3>
            <div class="countries-grid">
              <div class="country-group">
                <h4>{{ $t('viberOtp.stats.topCountries.cee') }}</h4>
                <div class="country-list">
                  <span class="country-item">Albania 80%</span>
                  <span class="country-item">Bosnia & Herzegovina 98%</span>
                  <span class="country-item">Bulgaria 94%</span>
                  <span class="country-item">Croatia 78%</span>
                  <span class="country-item">Georgia 71%</span>
                  <span class="country-item">Greece 91%</span>
                  <span class="country-item">Hungary 68%</span>
                  <span class="country-item">Macedonia 85%</span>
                  <span class="country-item">Montenegro 96%</span>
                  <span class="country-item">Serbia 97%</span>
                  <span class="country-item">Slovakia 40%</span>
                  <span class="country-item">Slovenia 88%</span>
                  <span class="country-item">Ukraine 98%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberOtp.stats.topCountries.cis') }}</h4>
                <div class="country-list">
                  <span class="country-item">Armenia 93%</span>
                  <span class="country-item">Belarus 86%</span>
                  <span class="country-item">Moldova 82%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberOtp.stats.topCountries.sea') }}</h4>
                <div class="country-list">
                  <span class="country-item">Maldives 76%</span>
                  <span class="country-item">Myanmar 67%</span>
                  <span class="country-item">Nepal 36%</span>
                  <span class="country-item">Philippines 71%</span>
                  <span class="country-item">Sri Lanka 24%</span>
                  <span class="country-item">Vietnam 26%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberOtp.features.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('viberOtp.features.description') }}</p>
          </div>

          <div class="features-grid">
            <div v-for="(feature, index) in tm('viberOtp.features.list')" :key="index" class="feature-card">
              <div class="feature-icon">
                <NuxtImg :src="rt(feature.icon)" :alt="rt(feature.title)" :width="100" :height="100" format="webp" loading="lazy" />
              </div>
              <h3 class="feature-title">{{ rt(feature.title) }}</h3>
              <p class="feature-description">{{ rt(feature.description) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section bg-gray-50" id="pricing">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberOtp.pricing.title') }}</div>
          <div class="section-head-description text-center">{{ $t('viberOtp.pricing.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-table-container">
            <ViberOtpRatesTable />
          </div>
        </div>
      </div>
    </div>

    <!-- API Documentation Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberOtp.api.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('viberOtp.api.description') }}</p>
          </div>

          <div class="api-examples">
            <div class="api-tabs">
              <button v-for="(example, index) in apiExamples" :key="index" @click="activeExample = index"
                class="api-tab" :class="{ 'active': activeExample === index }">
                {{ example.title }}
              </button>
            </div>

            <div class="api-content">
              <div class="api-description">
                <h4>{{ apiExamples[activeExample].title }}</h4>
                <p>{{ apiExamples[activeExample].description }}</p>
              </div>

              <div class="code-block">
                <div class="code-header">
                  <span class="code-language">{{ apiExamples[activeExample].language }}</span>
                  <button @click="copyCode" class="copy-button">
                    {{ $t('viberOtp.api.copyCode') }}
                  </button>
                </div>
                <pre><code>{{ apiExamples[activeExample].code }}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Templates Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberOtp.templates.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('viberOtp.templates.description') }}</p>
          </div>

          <div class="templates-grid">
            <div v-for="(template, index) in tm('viberOtp.templates.list')" :key="index" class="template-card">
              <div class="template-header">
                <h4 class="template-title">{{ replacedBrackets(rt(template.title)) }}</h4>
                <div class="template-id">{{ rt(template.id) }}</div>
              </div>
              <div class="template-content">
                <div class="template-text">{{ replacedBrackets(rt(template.text)) }}</div>
                <div class="template-params">
                  <h5>{{ $t('viberOtp.templates.parameters') }}</h5>
                  <ul>
                    <li v-for="(param, paramIndex) in template.params" :key="paramIndex">
                      <strong>{{ replacedBrackets(rt(param.name)) }}</strong> – {{ replacedBrackets(rt(param.type)) }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-content text-center">
          <div class="cta-content">
            <h2 class="section-head-title mb-4">{{ $t('viberOtp.cta.title') }}</h2>
            <p class="text-gray-600 mb-8">{{ $t('viberOtp.cta.description') }}</p>
            <div class="banner-action flex flex-wrap items-center justify-center gap-4">
              <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
                <p>{{ $t('viberOtp.cta.getStarted') }}</p>
              </NuxtLink>
              <NuxtLink external target="_blank" :to="'https://omni.smsbat.com/auth/login?regView=true'"
                class="button outlined">
                <p>{{ $t('viberOtp.cta.viewDocs') }}</p>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.otp-demo {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.phone-mockup {
  width: 200px;
  height: 400px;
  background: #333;
  border-radius: 25px;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.viber-header {
  background: #7B519D;
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.viber-logo {
  font-weight: bold;
  font-size: 1.1rem;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
}

.message-container {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.message {
  background: #f0f0f0;
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 10px;
}

.business-name {
  font-weight: bold;
  color: #7B519D;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.message-text {
  font-size: 0.9rem;
  margin-bottom: 10px;
  line-height: 1.4;
}

.message-icon {
  font-size: 0.8rem;
  margin: 0 2px;
  display: inline-block;
  vertical-align: middle;
}

.otp-code {
  font-size: 1.5rem;
  font-weight: bold;
  color: #4f46e5;
  text-align: center;
  margin: 10px 0;
  padding: 10px;
  background: white;
  border-radius: 8px;
  border: 2px dashed #4f46e5;
}

.security-note {
  font-size: 0.8rem;
  color: #666;
  font-style: italic;
  margin-top: 8px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.feature-card {
  text-align: center;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.feature-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.feature-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.feature-description {
  color: #718096;
  line-height: 1.6;
}

.pricing-table-container {
  overflow-x: auto;
  margin-top: 40px;
}

.pricing-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  th,
  td {
    padding: 15px 20px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #2d3748;
  }

  td {
    color: #718096;
  }

  .country-name {
    font-weight: 500;
    color: #2d3748;
  }

  .rate-price {
    font-weight: 600;
    color: #4f46e5;
  }

  .minimum-fee {
    font-weight: 500;
    color: #2d3748;
  }

  tr:hover {
    background: #f8f9fa;
  }
}

.api-examples {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.api-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.api-tab {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s ease;

  &.active {
    background: white;
    color: #4f46e5;
    border-bottom: 2px solid #4f46e5;
  }

  &:hover:not(.active) {
    background: #e9ecef;
  }
}

.api-content {
  padding: 30px;
}

.api-description {
  margin-bottom: 25px;

  h4 {
    font-size: 1.3rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 10px;
  }

  p {
    color: #718096;
    line-height: 1.6;
  }
}

.code-block {
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
}

.code-language {
  font-weight: 600;
  color: #495057;
}

.copy-button {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background: #5a67d8;
  }
}

.code-block pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #2d3748;
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.template-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.template-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.template-id {
  font-size: 0.8rem;
  color: #4f46e5;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.template-content {
  .template-text {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-style: italic;
    color: #495057;
    line-height: 1.5;
  }

  .template-params {
    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 10px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      padding: 5px 0;
      color: #718096;
      font-size: 0.9rem;

      strong {
        color: #2d3748;
      }
    }
  }
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;

  .section-head-title {
    color: white;
  }

  .text-gray-600 {
    color: rgba(255, 255, 255, 0.8);
  }
}

@media (max-width: 768px) {
  .main-banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .phone-mockup {
    width: 150px;
    height: 300px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .templates-grid {
    grid-template-columns: 1fr;
  }

  .api-tabs {
    flex-direction: column;
  }

  .pricing-table {
    font-size: 0.9rem;

    th,
    td {
      padding: 10px 15px;
    }
  }
}

@media (max-width: 480px) {
  .phone-mockup {
    width: 120px;
    height: 240px;
  }

  .template-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .pricing-table {
    font-size: 0.8rem;

    th,
    td {
      padding: 8px 12px;
    }
  }
}

/* Global Stats Section */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.stat-item {
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.top-countries h3 {
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.countries-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.country-group h4 {
  color: #3070b6;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.country-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.country-item {
  color: #6c757d;
  font-size: 0.9rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #3070b6;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .countries-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}
</style>